import { useEffect, useReducer, useRef } from "react";
import EventBus from "@/lib/eventBus";
import {
  AuthenticationContext,
  defaultAuthenticationContextData,
} from "./authContext";
import { reducer } from "./authContextReducer";
import { getChats, getCustomWorkspaces, getReports } from "@/lib/serverComm";

import type { AuthenticationContextData } from "@/types/authContext";

const findCurrentAccountId = (state: AuthenticationContextData) => {
  if (!state.userData || !state.userData.workspaces) {
    throw new Error("User data or workspaces not available");
  }
  const currentWorkspace = state.userData.workspaces.find(
    (workspace) => workspace.is_current,
  )?.domo_instance_domain;
  return currentWorkspace;
};

// Provider component
export function AuthContextProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [state, dispatch] = useReducer(
    reducer,
    defaultAuthenticationContextData,
  );

  // Track the latest state for event handlers to avoid stale closures.
  const stateRef = useRef(state);
  stateRef.current = state;

  // awaits are not allowed in the reducer so we use EventBus for async actions.
  // the EventBus will call dispatch after the async action is completed.
  useEffect(() => {
    const handleRefreshChats = async () => {
      const currentState = stateRef.current;
      const params = currentState.chatState.sidebarChats.params;
      const currentAccountId = findCurrentAccountId(currentState);
      const chats = await getChats(
        params.limit,
        params.query,
        params.offset,
        undefined,
        currentAccountId,
      );
      dispatch({ type: "setSidebarChatsAction", payload: chats });
    };

    EventBus.$on("sidebar:refreshChats", handleRefreshChats);
    return () => {
      EventBus.$off("sidebar:refreshChats", handleRefreshChats);
    };
  }, []);

  useEffect(() => {
    const handleRefreshCustomWorkspaces = async () => {
      const currentState = stateRef.current;
      const params =
        currentState.customWorkspaceState.sidebarCustomWorkspaces.params;
      const currentAccountId = findCurrentAccountId(currentState);
      const customWorkspaces = await getCustomWorkspaces(
        params.limit,
        params.query,
        params.offset,
        params.sort,
        currentAccountId,
      );
      dispatch({
        type: "setSidebarCustomWorkspacesAction",
        payload: customWorkspaces,
      });
    };

    EventBus.$on(
      "sidebar:refreshCustomWorkspaces",
      handleRefreshCustomWorkspaces,
    );
    return () => {
      EventBus.$off(
        "sidebar:refreshCustomWorkspaces",
        handleRefreshCustomWorkspaces,
      );
    };
  }, []);

  useEffect(() => {
    const handleLoadCustomWorkspaces = async () => {
      const currentState = stateRef.current;
      const params = currentState.customWorkspaceState.customWorkspaces.params;
      const customWorkspaces = await getCustomWorkspaces(
        params.limit,
        params.query,
        params.offset,
        params.sort,
        undefined,
      );
      dispatch({
        type: "setCustomWorkspacesAction",
        payload: customWorkspaces,
      });
    };

    EventBus.$on(
      "customWorkspaces:loadCustomWorkspaces",
      handleLoadCustomWorkspaces,
    );
    return () => {
      EventBus.$off(
        "customWorkspaces:loadCustomWorkspaces",
        handleLoadCustomWorkspaces,
      );
    };
  }, []);

  useEffect(() => {
    const handleRefreshReports = async () => {
      const currentState = stateRef.current;
      const params = currentState.reportState.sidebarReports.params;
      const currentAccountId = findCurrentAccountId(currentState);
      const reports = await getReports(
        params.limit,
        params.query,
        params.offset,
        false,
        currentAccountId,
      );
      dispatch({ type: "setSidebarReportsAction", payload: reports });
    };

    EventBus.$on("sidebar:refreshReports", handleRefreshReports);
    return () => {
      EventBus.$off("sidebar:refreshReports", handleRefreshReports);
    };
  }, []);

  return (
    <AuthenticationContext.Provider value={{ state, dispatch }}>
      {children}
    </AuthenticationContext.Provider>
  );
}
